import React, { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Loader2, Paperclip, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import type { IssueReport } from "../../types/issue-triage";
import { formatBytes, issueFileList } from "../../lib/issueTriage/issueFiles";
import type { IssueFileView } from "../../lib/issueTriage/issueFiles";
import { useIssueFileUpload, useRemoveIssueAttachment } from "../../hooks/issueTriage/useIssueFiles";
import type { FileOutcome, IssueFileUploader } from "../../hooks/issueTriage/useIssueFiles";

/** One honest line per outcome: what landed, and exactly which files did not and why. */
export function reportFileOutcomes(outcomes: FileOutcome[], issueLabel?: string): void {
  const added = outcomes.filter((o) => o.ok && !o.already).length;
  const already = outcomes.filter((o) => o.ok && o.already);
  const failed = outcomes.filter((o) => !o.ok);
  // The issue is named, so a person who moved to another issue while files were uploading knows where they landed.
  const where = issueLabel ? ` to ${issueLabel}` : "";
  if (added > 0) toast.success(added === 1 ? `File added${where}` : `${added} files added${where}`);
  for (const f of already) toast(`${f.name} is already on this issue. Nothing was added.`);
  for (const f of failed) toast.error(f.message ?? `${f.name} was not added.`);
}

const pastedName = (f: File): string =>
  f.name && f.name !== "image.png" ? f.name : `Pasted screenshot ${new Date().toLocaleTimeString().replace(/[^0-9A-Za-z]+/g, "-")}.png`;

/**
 * Every file and screenshot on one issue, at ANY stage (L-1224 part 2): the reporter's and the team's images as large
 * thumbnails, other files (pdf, logs) as named rows; add by button, by dropping files on it, or by pasting a
 * screenshot; remove each with a one-click confirm. Names are shown as plain text; links open in a new tab.
 */
export function IssueFilesPanel({ issue }: { issue: IssueReport }) {
  return <IssueFilesPanelView issue={issue} uploader={useIssueFileUpload(issue)} />;
}

/**
 * The same panel with its uploader handed in, so the detail page's paperclip and this panel share ONE upload loop and
 * ONE busy flag (two loops could otherwise run at once and toast on the wrong issue).
 */
export function IssueFilesPanelView({ issue, uploader }: { issue: IssueReport; uploader: IssueFileUploader }) {
  const { upload, busy } = uploader;
  const remove = useRemoveIssueAttachment();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState<string | null>(null);
  const files = issueFileList(issue).filter((f) => f.source !== "capture");

  const addFiles = useCallback(
    async (list: File[]) => {
      if (list.length === 0) return;
      if (busy) {
        // Never drop a file silently: say so (the paste handler has already swallowed the paste).
        toast.error("Still adding files. Try again in a moment.");
        return;
      }
      reportFileOutcomes(await upload(list), issue.issueCode || undefined);
    },
    [busy, upload, issue.issueCode],
  );

  // Paste a screenshot anywhere on the issue page (text pastes carry no files and are left alone).
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const pasted = Array.from(e.clipboardData?.files ?? []);
      if (pasted.length === 0) return;
      e.preventDefault();
      void addFiles(pasted.map((f) => new File([f], pastedName(f), { type: f.type })));
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addFiles]);

  const doRemove = async (f: IssueFileView) => {
    setConfirming(null);
    try {
      await remove.mutateAsync({ issueId: issue.id, url: f.url });
      toast.success("File removed from this issue");
    } catch {
      toast.error(`${f.name} could not be removed. It is still on the issue.`);
    }
  };

  // Only files the TEAM added can be removed. The reporter's own screenshot and images are the evidence of what was
  // reported: the server refuses to take them off, so no button is offered for them.
  const removeControl = (f: IssueFileView) => {
    if (f.source !== "team") return null;
    const keepsLink = "Takes it off this issue. The stored file is kept and its link still opens it. There is no undo button; a developer can restore it from the audit entry.";
    return confirming === f.key ? (
      <span className="flex items-center gap-1 rounded bg-black/80 px-1.5 py-1 text-xs text-white" role="group" aria-label={`Remove ${f.name}?`}>
        {f.isImage ? "Remove?" : "Remove this file? Anyone with its link can still open it."}
        <button className="rounded bg-red-600 px-1.5 py-0.5 font-medium" title={keepsLink} onClick={() => void doRemove(f)}>Remove</button>
        <button className="rounded bg-slate-600 px-1.5 py-0.5" onClick={() => setConfirming(null)}>Keep</button>
      </span>
    ) : (
      <button
        onClick={() => setConfirming(f.key)}
        aria-label={`Remove ${f.name}`}
        title="Remove from this issue (the stored file is kept)"
        className="rounded bg-black/60 p-1 text-white/80 hover:bg-red-600 hover:text-white"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    );
  };

  return (
    <div
      className={`border-t border-slate-700 p-3 ${dragging ? "bg-blue-900/40 ring-2 ring-[#3FA5FF]" : ""}`}
      data-testid="issue-attachments"
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        const dropped = Array.from(e.dataTransfer?.files ?? []);
        setDragging(false);
        if (dropped.length > 0) {
          e.preventDefault();
          void addFiles(dropped);
        }
      }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Files &amp; screenshots ({files.length})</p>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="flex items-center gap-1 rounded-md bg-slate-700 px-2 py-1 text-xs font-medium text-white hover:bg-slate-600 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {busy ? "Adding..." : "Add files"}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" aria-label="Choose files to add" data-testid="issue-file-input"
          onChange={(e) => {
            const chosen = Array.from(e.target.files ?? []);
            e.target.value = "";
            void addFiles(chosen);
          }} />
      </div>
      <p className="mb-2 text-xs text-slate-500" aria-live="polite">
        {busy ? "Uploading..." : "Drop files here, or paste a screenshot (Ctrl+V). Images, PDFs and logs, up to 20 MB each."}
      </p>

      {files.length === 0 ? (
        <p className="text-xs text-slate-500">No other files on this issue yet.</p>
      ) : (
        <div className="flex max-h-80 flex-wrap gap-3 overflow-y-auto">
          {files.map((f) =>
            f.isImage ? (
              <div key={f.key} className="group relative shrink-0 overflow-hidden rounded-lg ring-1 ring-slate-600 hover:ring-[#3FA5FF]">
                <a href={f.url} target="_blank" rel="noopener noreferrer" title={`Open ${f.name}`}>
                  <img src={f.url} alt={f.name} className="h-40 w-56 object-cover" />
                </a>
                <div className="absolute right-1 top-1">{removeControl(f)}</div>
                {f.source === "team" && (
                  <p className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white/80">{f.name}</p>
                )}
              </div>
            ) : (
              <div key={f.key} className="flex w-56 shrink-0 items-start gap-2 rounded-lg bg-slate-800 p-2 ring-1 ring-slate-600">
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <a href={f.url} target="_blank" rel="noopener noreferrer" className="block truncate text-sm font-medium text-blue-300 hover:underline" title={`Open ${f.name}`}>
                    {f.name}
                  </a>
                  <p className="truncate text-[11px] text-slate-400">
                    {[f.contentType, formatBytes(f.size), f.addedByName ? `added by ${f.addedByName}` : ""].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="shrink-0">{removeControl(f)}</div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

